import * as React from "react";
import { AlertCircle, Calendar as CalendarIcon, Check, ChevronsUpDown, Globe } from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import type { Locale as DfLocale } from "date-fns";


import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FlagIcon } from "@/components/site/FlagIcon";
import {
  DIAL_CODES,
  detectDialPrefix,
  joinPhone,
  phoneCountryList,
  splitPhone,
} from "@/lib/phone";


import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const CONTROL_CLASS =
  "h-11 w-full rounded-md border-border bg-background text-sm shadow-none";

/** ISO-datum (YYYY-MM-DD) till lokalt Date utan tidszonshopp. */
function parseIsoDate(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Date till ISO-datum i lokal tid. */
function toIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export type Option = {
  value: string;
  label: string;
  group?: string;
  flagCode?: string;
  flagEmoji?: string;
  /** Visar märket "Föreslagen" i listan. */
  suggested?: boolean;
  /** Text som märket visar. Utan värde används "Föreslagen". */
  suggestedLabel?: string;
};


/** Märket som visar vilket val vi föreslagit. */
function SuggestedBadge({ label }: { label: string }) {
  return (
    <span className="ml-2 shrink-0 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.625rem] font-medium tracking-wide text-primary">
      {label}
    </span>
  );
}



/** Samma utseende på alla felmeddelanden, var de än visas. */
export function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <span className="mt-1.5 flex items-start gap-1.5 text-xs leading-relaxed text-destructive">
      <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}


export function Field({
  label,
  labelAside,
  labelTooltip,
  htmlFor,
  hint,
  warning,
  error,
  required,
  anchorId,
  children,
}: {
  label: string;
  labelAside?: string | undefined;
  labelTooltip?: string | undefined;
  htmlFor?: string;
  hint?: string | undefined;
  warning?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  anchorId?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0" {...(anchorId ? { id: anchorId } : {})}>
      <div className="mb-1.5 flex min-h-5 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        {labelTooltip ? (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <label
                  htmlFor={htmlFor}
                  tabIndex={0}
                  className="flex items-center gap-1.5 text-sm font-medium decoration-dotted underline-offset-4 hover:underline focus-visible:underline"
                >
                  <span>
                    {label}
                    {required ? <span className="ml-1 text-primary">*</span> : null}
                  </span>
                </label>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-xs">
                <p>{labelTooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-sm font-medium">
            <span>
              {label}
              {required ? <span className="ml-1 text-primary">*</span> : null}
            </span>
          </label>
        )}
        {labelAside ? (
          <span className="text-xs leading-5 text-muted-foreground">{labelAside}</span>
        ) : null}
      </div>
      {children}
      {hint && !error && !warning ? (
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{hint}</span>
      ) : null}
      {warning && !error ? (
        <span className="mt-1.5 flex items-start gap-1.5 text-xs leading-relaxed text-warning-foreground">
          <AlertCircle className="mt-px size-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>{warning}</span>
        </span>
      ) : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}


export function TextField({
  label,
  labelAside,
  labelTooltip,
  value,
  onChange,
  onBlur,
  hint,
  warning,
  error,
  required,
  type = "text",
  placeholder,
  disabled,
  inputMode,
  maxLength,
  anchorId,
  autoComplete,
  name,
}: {
  label: string;
  labelAside?: string | undefined;
  labelTooltip?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hint?: string | undefined;
  warning?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: "text" | "numeric" | "tel" | "email";
  maxLength?: number;
  anchorId?: string | undefined;
  autoComplete?: string;
  name?: string;
}) {
  const id = React.useId();
  return (
    <Field
      label={label}
      labelAside={labelAside}
      labelTooltip={labelTooltip}
      htmlFor={id}
      hint={hint}
      warning={warning}
      error={error}
      required={required ?? false}
      anchorId={anchorId}
    >

      <Input
        id={id}
        type={type}
        aria-invalid={error ? true : undefined}
        className={cn(CONTROL_CLASS, error && "border-destructive focus-visible:ring-destructive/30")}
        value={value}
        placeholder={placeholder ?? ""}
        disabled={disabled ?? false}
        inputMode={inputMode ?? "text"}
        maxLength={maxLength ?? 200}
        autoComplete={autoComplete ?? "on"}
        {...(name ? { name } : {})}
        onChange={(e) => onChange(e.target.value)}
        {...(onBlur ? { onBlur } : {})}
      />
    </Field>
  );
}

/** Telefonfält med obligatorisk landskodsväljare. Värdet lagras som +<landskod><nummer>. */
export function PhoneField({
  label,
  value,
  onChange,
  onBlur,
  hint,
  error,
  required,
  anchorId,
  placeholder,
  searchPlaceholder = "Sök land…",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  anchorId?: string | undefined;
  placeholder?: string;
  searchPlaceholder?: string;
}) {
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const parsed = splitPhone(value, "");
  // Ingen landskod är förvald – besökaren väljer aktivt ett land innan
  // numret kan normaliseras till +<landskod><nummer>.
  const [country, setCountry] = React.useState(parsed.country);
  const active = value ? parsed.country : country;
  const local = parsed.local;
  const dial = active ? DIAL_CODES[active] : undefined;
  const list = React.useMemo(() => phoneCountryList(), []);
  const [activeKey, setActiveKey] = React.useState("");
  // Rutan visar en egen text under pågående inmatning så att inget skrivet
  // tecken (t.ex. "+46…") suddas eller flyttar markören. Texten speglas bara
  // från värdet när ändringen kommer utifrån (t.ex. valt land i listan).
  const [text, setText] = React.useState(local);
  const lastSent = React.useRef(value);
  React.useEffect(() => {
    if (value === lastSent.current) return;
    lastSent.current = value;
    setText(splitPhone(value, "").local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);


  // Nummer som kommer från chatten eller ett sparat utkast kan sakna landskod
  // ("070-123 45 67"). Normalisera direkt – men bara om användaren valt ett
  // land, så att vi aldrig tvingar på en landskod.
  React.useEffect(() => {
    const raw = (value ?? "").trim();
    if (!raw || raw.startsWith("+")) return;
    if (!country) return;
    const normalized = joinPhone(country, parsed.local);
    if (normalized && normalized !== raw) onChange(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const groups = React.useMemo(() => {
    const map = new Map<string, typeof list>();
    for (const item of list) {
      const arr = map.get(item.group) ?? [];
      arr.push(item);
      map.set(item.group, arr);
    }
    return [...map.entries()];
  }, [list]);

  return (
    <Field
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required ?? false}
      anchorId={anchorId}
    >
      <div
        className={cn(
          "flex w-full items-stretch overflow-hidden rounded-md border border-input bg-background transition-colors",
          "focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25",
          error && "border-destructive focus-within:border-destructive focus-within:ring-destructive/25",
        )}
      >
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (next) {
              const current = list.find(({ country: c }) => c.code === active)?.country;
              setActiveKey(current ? `${current.name} +${current.dial} ${current.code}` : "");
            }
            setOpen(next);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-label="Landskod"
              className="flex h-11 shrink-0 items-center gap-1.5 border-r border-input px-2.5 text-sm outline-none hover:bg-muted/60 focus:outline-none focus-visible:outline-none"
            >
              {dial ? (
                <>
                  <FlagIcon code={active} emoji="" size={20} />
                  <span className="tabular-nums">+{dial}</span>
                </>
              ) : (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Globe className="size-4" />
                  <span>Landskod</span>
                </span>
              )}
              <ChevronsUpDown className="size-3.5 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="pointer-events-auto w-64 p-0" align="start">
            <Command value={activeKey} onValueChange={setActiveKey}>
              <CommandInput placeholder={searchPlaceholder} />

              <CommandList className="max-h-64">
                <CommandEmpty>Ingen träff.</CommandEmpty>
                {groups.map(([group, items]) => (
                  <CommandGroup key={group} heading={group}>
                    {items.map(({ country: c }) => (
                      <CommandItem
                        key={`${group}-${c.code}`}
                        value={`${c.name} +${c.dial} ${c.code}`}
                        onSelect={() => {
                          setCountry(c.code);
                          onChange(local ? joinPhone(c.code, local) : "");
                          setOpen(false);
                        }}
                      >
                        <FlagIcon code={c.code} emoji={c.flag} size={20} className="mr-2" />
                        <span className="truncate">{c.name}</span>
                        <span className="ml-auto tabular-nums text-muted-foreground">
                          +{c.dial}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          aria-invalid={error ? true : undefined}
          className="h-11 flex-1 rounded-none border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
          value={text}
          placeholder={placeholder ?? "Telefonnummer"}
          maxLength={24}
          onChange={(e) => {
            const typed = e.target.value;
            setText(typed);
            const trimmed = typed.trim();
            const d = typed.replace(/\D+/g, "");
            // Skriver eller klistrar användaren in landskod själv ("+46…" eller
            // "0046…") lagras numret som +<siffror> direkt – landet känns igen
            // av splitPhone och flaggan tänds utan att något kapas eller hoppar.
            let next: string;
            if (trimmed.startsWith("+")) {
              next = d ? `+${d}` : "";
            } else if (trimmed.startsWith("00")) {
              next = d.length > 2 ? `+${d.slice(2)}` : "";
            } else {
              // Behåll inledande nolla medan man skriver – den städas först när
              // man lämnar rutan, så fältet aldrig ändrar sig under inmatningen.
              next = active ? (d ? `+${DIAL_CODES[active]}${d}` : "") : d;
            }
            lastSent.current = next;
            onChange(next);
          }}
          onBlur={() => {
            const d = (text ?? "").replace(/\D+/g, "");
            if (d) {
              // Landsnummer inskrivet i nummerfältet: "+<dial>…" och
              // "00<dial>…" känns alltid igen, även när flaggan redan hunnit ändras.
              const trimmed = (text ?? "").trim();
              const hit =
                trimmed.startsWith("+") || trimmed.startsWith("00") || !active
                  ? detectDialPrefix(d)
                  : null;
              if (hit) {
                setCountry(hit.country);
                const next = joinPhone(hit.country, hit.local);
                lastSent.current = next;
                onChange(next);
                setText(hit.local);
              } else if (active) {
                const next = joinPhone(active, d);
                lastSent.current = next;
                onChange(next);
                setText(splitPhone(next, "").local);
              }
            }
            onBlur?.();
          }}
        />
      </div>
    </Field>
  );
}


export function TextareaField({
  label,
  value,
  onChange,
  onBlur,
  hint,
  error,
  required,
  rows,
  anchorId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  rows?: number;
  anchorId?: string | undefined;
}) {
  const id = React.useId();
  return (
    <Field
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required ?? false}
      anchorId={anchorId}
    >
      <Textarea
        id={id}
        rows={rows ?? 5}
        aria-invalid={error ? true : undefined}
        className={cn(
          "min-h-28 w-full rounded-md border-border bg-background text-sm shadow-none",
          error && "border-destructive focus-visible:ring-destructive/30",
        )}
        value={value}
        maxLength={2000}
        onChange={(e) => onChange(e.target.value)}
        {...(onBlur ? { onBlur } : {})}
      />
    </Field>
  );
}



export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = "Välj",
  searchable,
  hint,
  error,
  required,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  /** Tvinga sökbar lista. Utan värde blir listan sökbar automatiskt vid fler än tio val. */
  searchable?: boolean;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  disabled?: boolean;
}) {
  const id = React.useId();
  const useSearch = searchable ?? options.length > 10;

  if (useSearch && !disabled) {
    return (
      <ComboboxField
        label={label}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        hint={hint}
        error={error}
        required={required ?? false}
      />
    );
  }

  const groups: [string, Option[]][] = [];
  for (const o of options) {
    const key = o.group ?? "";
    const last = groups[groups.length - 1];
    if (last && last[0] === key) last[1].push(o);
    else groups.push([key, [o]]);
  }

  return (
    <Field label={label} htmlFor={id} hint={hint} error={error} required={required ?? false}>
      {/*
        Alltid kontrollerad – annars tappar fältet ett återställt värde.
        Tomt värde ignoreras: webbläsarens formåterställning vid omladdning
        skickar ett tomt "change" som annars nollställer valet.
      */}
      <Select
        value={value ?? ""}
        onValueChange={(v) => {
          if (v) onChange(v);
        }}
        disabled={disabled ?? false}
      >
        <SelectTrigger
          id={id}
          aria-invalid={error ? true : undefined}
          className={cn(
            CONTROL_CLASS,
            "min-w-0",
            error && "border-destructive",
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {groups.map(([group, items], gi) => (
            <SelectGroup key={group || gi}>
              {group ? <SelectLabel>{group}</SelectLabel> : null}
              {items.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  <span className="flex min-w-0 items-center gap-2 text-sm">
                    {o.flagCode ? (
                      <FlagIcon code={o.flagCode} emoji={o.flagEmoji ?? ""} size={20} />
                    ) : null}
                    <span className="truncate">{o.label}</span>
                    {o.suggested ? <SuggestedBadge label={o.suggestedLabel ?? "Föreslagen"} /> : null}
                  </span>
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>

    </Field>
  );
}

export function ComboboxField({
  label,
  value,
  onChange,
  options,
  placeholder = "Välj",
  searchPlaceholder = "Sök…",
  hint,
  error,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const fieldId = React.useId();
  const selected = options.find((o) => o.value === value);
  const groups = React.useMemo(() => {
    const map = new Map<string, Option[]>();
    for (const o of options) {
      const key = o.group ?? "";
      const list = map.get(key) ?? [];
      list.push(o);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [options]);

  const selectedKey = selected ? `${selected.label} ${selected.value}` : "";
  const [activeKey, setActiveKey] = React.useState(selectedKey);

  return (
    <Field label={label} htmlFor={fieldId} hint={hint} error={error} required={required ?? false}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) setActiveKey(selectedKey);
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={fieldId}
            type="button"
            variant="outline"

            role="combobox"
            aria-expanded={open}
            aria-invalid={error ? true : undefined}
            className={cn(
              CONTROL_CLASS,
              "justify-between px-3 font-normal",
              !selected && "text-muted-foreground",
              error && "border-destructive",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              {selected?.flagCode ? (
                <FlagIcon code={selected.flagCode} emoji={selected.flagEmoji ?? ""} size={22} />
              ) : null}
              <span className="truncate">
                {selected ? selected.label : placeholder}
              </span>
            </span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="pointer-events-auto w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command value={activeKey} onValueChange={setActiveKey}>
            <CommandInput placeholder={searchPlaceholder} />

            <CommandList className="max-h-64">
              <CommandEmpty>Inget träff.</CommandEmpty>
              {groups.map(([group, items], gi) => (
                <CommandGroup key={group || gi} heading={group || undefined}>
                  {items.map((o) => (
                    <CommandItem
                      key={`${group}-${o.value}`}
                      value={`${o.label} ${o.value}`}
                      onSelect={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 size-4",
                          value === o.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {o.flagCode ? (
                        <FlagIcon
                          code={o.flagCode}
                          emoji={o.flagEmoji ?? ""}
                          size={20}
                          className="mr-2"
                        />
                      ) : null}
                      <span className="truncate text-sm">{o.label}</span>
                      {o.suggested ? <SuggestedBadge label={o.suggestedLabel ?? "Föreslagen"} /> : null}

                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Field>
  );
}

export function CheckboxField(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
  hint?: string;
  disabled?: boolean;
  tooltip?: string;
  learnMore?: React.ReactNode;
  compact?: boolean;
  error?: string | undefined;
}) {
  const {
    checked,
    onChange,
    children,
    hint,
    disabled,
    tooltip,
    learnMore,
    compact = false,
    error,
  } = props;
  

  const id = React.useId();

  const box = (
    <div
      className={cn(
        "flex min-h-11 flex-col justify-center rounded-lg border border-border bg-background px-3 py-2 transition-colors",
        disabled ? "opacity-60" : "cursor-pointer hover:border-primary/60",
      )}
      tabIndex={tooltip ? 0 : undefined}
      onClick={(e) => {
        if (disabled) return;
        // Klick i hela rutan bockar i valet – utom på länkar/knappar som "Läs mer".
        const target = e.target as HTMLElement | null;
        if (target?.closest("button, a, input, textarea, [role='button']")) return;
        onChange(!checked);
      }}
    >
      <div
        className={cn(
          "flex w-full gap-3 items-start",
        )}
      >
        {/* Rutan centreras optiskt mot etikettens första rad (leading-5). */}
        <span className="flex h-5 shrink-0 items-center">
          <Checkbox
            id={id}
            checked={checked}
            disabled={disabled ?? false}
            onCheckedChange={(v) => onChange(v === true)}
            aria-invalid={error ? true : undefined}
            className={cn(
              "size-5 shrink-0",
              error && "border-destructive ring-2 ring-destructive/30",
            )}
          />
        </span>
        <div className="min-w-0 flex-1">
          <label
            htmlFor={id}
            onClick={(e) => e.preventDefault()}
            className={cn(
              "block leading-5",
              compact ? "text-xs sm:text-sm" : "text-sm",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
            )}
          >

            {children}
          </label>
          {hint || learnMore ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              {hint ? <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
              {learnMore}
            </div>
          ) : null}
          {error ? (
            <div className="mt-1">
              <FieldError>{error}</FieldError>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  if (!tooltip) return box;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{box}</TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-xs">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}


/** Datumväljare med samma höjd som övriga fält och spärrade dagar. */
export function DateField({
  value,
  onChange,
  min,
  max,
  isDisabledDay,
  locale = "sv",
  placeholder = "Välj datum",
  className,
  error,
  defaultOpen = false,
}: {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  isDisabledDay?: (iso: string) => boolean;
  locale?: "sv" | "en" | "es";
  placeholder?: string;
  className?: string;
  error?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [dfLocale, setDfLocale] = React.useState<DfLocale | undefined>(undefined);

  React.useEffect(() => {
    let alive = true;
    void import("date-fns/locale").then((m) => {
      if (!alive) return;
      const pick = locale === "en" ? m.enGB : locale === "es" ? m.es : m.sv;
      setDfLocale(pick as DfLocale);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  const selected = value ? parseIsoDate(value) : undefined;
  const defaultMonth = selected ?? (min ? parseIsoDate(min) : undefined);

  const disabled = React.useCallback(
    (date: Date) => {
      const iso = toIsoDate(date);
      if (min && iso < min) return true;
      if (max && iso > max) return true;
      return isDisabledDay ? isDisabledDay(iso) : false;
    },
    [min, max, isDisabledDay],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            CONTROL_CLASS,
            "justify-start border px-3 font-normal",
            !value && "text-muted-foreground",
            error && "border-destructive",
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4 shrink-0 opacity-70" aria-hidden />
          <span className="truncate">{value || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          {...(selected ? { selected } : {})}
          {...(defaultMonth ? { defaultMonth } : {})}
          {...(min && parseIsoDate(min) ? { startMonth: parseIsoDate(min)! } : {})}
          {...(max && parseIsoDate(max) ? { endMonth: parseIsoDate(max)! } : {})}
          disabled={disabled}
          {...(dfLocale ? { locale: dfLocale } : {})}
          weekStartsOn={1}
          onSelect={(d) => {
            if (!d) return;
            onChange(toIsoDate(d));
            setOpen(false);
          }}
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}
